"use client";

/**
 * Day Chip
 * Presentational day cell for a schedule day strip: short weekday over a day number, with an
 * active (selected) treatment driven by the page primary color. Stateless — the parent owns
 * selection and layout. Shared by the Agenda view; the Calendar view keeps its own paged strip.
 */

interface DayChipProps {
  /** Short weekday, e.g. "Mon" (rendered uppercase). */
  weekdayShort: string;
  /** Day-of-month number, e.g. "12". */
  dayNumber: string;
  /** Whether this chip is the selected day. */
  isActive: boolean;
  /** Primary color used as the active chip background. */
  activeColor: string;
  /** Text color to use on the active chip (contrast-correct for `activeColor`). */
  activeTextColor: string;
  /** Accessible label for the chip, e.g. "Monday, August 12". */
  ariaLabel: string;
  /** Smaller hit target for dense fixed calendar chrome. */
  compact?: boolean;
  onClick: () => void;
}

/**
 * A single selectable day cell in a day strip.
 *
 * @param props - See DayChipProps.
 * @returns The day chip button.
 */
export function DayChip({
  weekdayShort,
  dayNumber,
  isActive,
  activeColor,
  activeTextColor,
  ariaLabel,
  compact = false,
  onClick,
}: DayChipProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      aria-label={ariaLabel}
      className={`flex shrink-0 touch-manipulation flex-col items-center justify-center gap-0.5 rounded-lg transition-colors ${
        compact ? "min-h-9 min-w-10 px-2 py-1" : "min-h-11 min-w-11 px-3 py-2"
      } ${isActive ? "" : "text-gray-700 hover:bg-gray-100"}`}
      style={isActive ? { backgroundColor: activeColor, color: activeTextColor } : undefined}
    >
      <span
        className={`text-[10px] font-semibold uppercase tracking-wider ${isActive ? "opacity-80" : "text-gray-500"}`}
      >
        {weekdayShort}
      </span>
      <span className="text-sm font-semibold tabular-nums">{dayNumber}</span>
    </button>
  );
}
